import React, { createContext, useContext, ReactNode } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import type { GeneralSettings, AdvancedSettings, Attribute, ObjectType } from '../../../shared/types';

interface MappingConfigurationContextType {
  // State values
  activeTab: number;
  generalSettings: GeneralSettings;
  advancedSettings: AdvancedSettings;
  selectedObjectType: string;
  processConditionsExpanded: boolean;
  projectPropertiesExpanded: boolean;
  processConditionsChecked: boolean;
  projectPropertiesChecked: boolean;
  currentAttributes: Attribute[];
  objectTypeOptions: ObjectType[];
  revitParameterOptions: { value: string; label: string; group: string }[];

  // Process conditions and project properties values
  processConditionsValue: { value: string };
  projectPropertiesValue: { value: string };
  // Process conditions attributes
  pipingSpecValuePC: { value: string };
  tracingSpecValuePC: { value: string };
  attribute12Value: { value: string };
  attribute13Value: { value: string };
  attribute14Value: { value: string };
  attribute15Value: { value: string };
  attribute16Value: { value: string };
  attribute17Value: { value: string };
  attribute18Value: { value: string };
  attribute19Value: { value: string };
  attribute20Value: { value: string };
  attribute21Value: { value: string };
  attribute22Value: { value: string };
  attribute23Value: { value: string };
  // Project properties attributes
  projectIdentifierValue: { value: string };
  projectNameValue: { value: string };
  areaValue: { value: string };
  attribute1ClientName: { value: string };
  attribute2ProjectAddress: { value: string };
  attribute3ClientDepartment: { value: string };
  attribute4ClientProjectNumber: { value: string };
  attribute5ClientDocumentNumber: { value: string };
  attribute6ClientCode: { value: string };
  attribute7EngPartyOffice: { value: string };
  attribute8EngPartyOfficeLocation: { value: string };
  attribute9EngPartyProjectNumber: { value: string };
  attribute10EngPartyAreaCode: { value: string };
  // General attributes
  skeyValue: { value: string };
  objectTypeNameValue: { value: string };
  pipingSpecValue: { value: string };
  systemGroupingValue: { value: string };
  insulationSpecValue: { value: string };
  tracingSpecValue: { value: string };
  weightValue: { value: string };
  insulationThicknessValue: { value: string };

  // Actions with accessibility features
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  handleObjectTypeChange: (event: SelectChangeEvent<string>) => void;
  handleAttributeChange: (order: number, field: keyof Attribute, value: string) => void;
  handleAddAttribute: () => void;
  handleRemoveAttribute: (order: number) => void;
  handleAddObjectType: () => void;
  handleCenterOfCoordinatesChange: (value: 'ProjectBasePoint' | 'SurveyPoint') => void;
  handleProcessConditionsChange: (checked: boolean) => void;
  handleProjectPropertiesChange: (checked: boolean) => void;

  // Value update handlers
  onProcessConditionsValueChange: (value: string) => void;
  onProjectPropertiesValueChange: (value: string) => void;
  onPipingSpecValuePCChange: (value: string) => void;
  onTracingSpecValuePCChange: (value: string) => void;
  onAttribute12ValueChange: (value: string) => void;
  onAttribute13ValueChange: (value: string) => void;
  onAttribute14ValueChange: (value: string) => void;
  onAttribute15ValueChange: (value: string) => void;
  onAttribute16ValueChange: (value: string) => void;
  onAttribute17ValueChange: (value: string) => void;
  onAttribute18ValueChange: (value: string) => void;
  onAttribute19ValueChange: (value: string) => void;
  onAttribute20ValueChange: (value: string) => void;
  onAttribute21ValueChange: (value: string) => void;
  onAttribute22ValueChange: (value: string) => void;
  onAttribute23ValueChange: (value: string) => void;
  onProjectIdentifierValueChange: (value: string) => void;
  onProjectNameValueChange: (value: string) => void;
  onAreaValueChange: (value: string) => void;
  onAttribute1ClientNameChange: (value: string) => void;
  onAttribute2ProjectAddressChange: (value: string) => void;
  onAttribute3ClientDepartmentChange: (value: string) => void;
  onAttribute4ClientProjectNumberChange: (value: string) => void;
  onAttribute5ClientDocumentNumberChange: (value: string) => void;
  onAttribute6ClientCodeChange: (value: string) => void;
  onAttribute7EngPartyOfficeChange: (value: string) => void;
  onAttribute8EngPartyOfficeLocationChange: (value: string) => void;
  onAttribute9EngPartyProjectNumberChange: (value: string) => void;
  onAttribute10EngPartyAreaCodeChange: (value: string) => void;
  // General attribute handlers
  onSkeyValueChange: (value: string) => void;
  onObjectTypeNameValueChange: (value: string) => void;
  onPipingSpecValueChange: (value: string) => void;
  onSystemGroupingValueChange: (value: string) => void;
  onInsulationSpecValueChange: (value: string) => void;
  onTracingSpecValueChange: (value: string) => void;
  onWeightValueChange: (value: string) => void;
  onInsulationThicknessValueChange: (value: string) => void;

  // Property handlers
  handlePipingSystemPropertyChange: (name: string, value: string) => void;
  handleProjectPropertyChange: (name: string, value: string) => void;

  // Configuration management
  saveConfiguration: () => Promise<void>;
  validateConfiguration: () => { isValid: boolean; errors: string[] };

  // State setters for collapsible sections
  setProcessConditionsExpanded: (expanded: boolean) => void;
  setProjectPropertiesExpanded: (expanded: boolean) => void;

  // Accessibility helpers
  getTabAriaProps: (tabIndex: number) => {
    id: string;
    'aria-controls': string;
  };
  getPanelAriaProps: (tabIndex: number) => {
    id: string;
    'aria-labelledby': string;
    role: 'tabpanel';
  };
  getObjectTypeAriaProps: (objectType: string) => {
    id: string;
    'aria-label': string;
  };
  getAttributeAriaProps: (attribute: Attribute) => {
    id: string;
    'aria-label': string;
  };
}

const MappingConfigurationContext = createContext<MappingConfigurationContextType | undefined>(undefined);

interface MappingConfigurationProviderProps {
  children: ReactNode;
  value: MappingConfigurationContextType;
}

export const MappingConfigurationProvider: React.FC<MappingConfigurationProviderProps> = ({ children, value }) => {
  return (
    <MappingConfigurationContext.Provider value={value}>
      {children}
    </MappingConfigurationContext.Provider>
  );
};

export const useMappingConfigurationContext = (): MappingConfigurationContextType => {
  const context = useContext(MappingConfigurationContext);
  if (!context) {
    throw new Error('useMappingConfigurationContext must be used within a MappingConfigurationProvider');
  }
  return context;
};
